import {FC, ReactNode, useCallback, useEffect, useMemo, useState} from 'react';
import {Bar} from 'react-chartjs-2';
import {styled} from 'styled-components';

import {GptCacheStrategy} from '@shared/api/app_api';

import {Button, NavButton} from '@shared-web/components/core/button';
import {ChatGptMarkdown} from '@shared-web/components/core/chat_gpt_markdown';
import {SvgIcon} from '@shared-web/components/core/svg_icon';
import {boyIcon} from '@shared-web/components/icons/boy_icon';
import {girlIcon} from '@shared-web/components/icons/girl_icon';
import {notifyError} from '@shared-web/lib/notification';

import {NameNavBar} from '@src/components/name_nav_bar';
import {NameSpellings} from '@src/components/name_spellings';
import {TopBarApp} from '@src/components/top_bar_app';
import {apiCall} from '@src/lib/api';
import {ParsedDb} from '@src/lib/db_store';
import {ParsedProject} from '@src/lib/projects_stores';
import {Palette} from '@src/theme';

interface NameViewProps {
  db: ParsedDb | undefined;
  nameKey: string;
  backPath: string;
  navPathPrefix: string;
  project?: ParsedProject;
  bottom?: ReactNode;
}

export const NameView: FC<NameViewProps> = ({
  db,
  nameKey,
  backPath,
  bottom,
  project,
  navPathPrefix,
}) => {
  const dbName = useMemo(() => db?.find(n => n.key === nameKey), [db, nameKey]);
  const bestName = useMemo(() => dbName?.totalByName[0]?.name, [dbName?.totalByName]);

  const [gptInfo, setGptInfo] = useState<string | undefined>();
  const handleGptClick = useCallback(async () => {
    if (bestName === undefined) {
      return;
    }
    const {markdown} = await apiCall('GET /name-info', {
      name: bestName,
      cacheStrategy: GptCacheStrategy.CACHE_THEN_REFRESH,
    });
    setGptInfo(markdown);
  }, [bestName]);

  useEffect(() => {
    if (bestName === undefined) {
      return;
    }
    apiCall('GET /name-info', {
      name: bestName,
      cacheStrategy: GptCacheStrategy.CACHE_ONLY,
    })
      .then(({markdown}) => setGptInfo(markdown))
      .catch(notifyError);
  }, [bestName]);
  const sexColor = useMemo(
    () => (dbName ? (dbName.sex === 'boy' ? '#7eb7c0' : '#c487b9') : '#888'),
    [dbName]
  );

  if (!db || !dbName || bestName === undefined) {
    return <></>;
  }

  const countByYears = Object.entries(dbName.totalByYear).sort(
    (e1, e2) => parseFloat(e1[0]) - parseFloat(e2[0])
  );

  const sexIcon =
    dbName.sex === 'boy' ? (
      <SvgIcon icon={boyIcon} color={sexColor} size="2rem" />
    ) : (
      <SvgIcon icon={girlIcon} color={sexColor} size="2rem" />
    );

  return (
    <TopBarApp
      left={<NavButton to={backPath}>Retour</NavButton>}
      center={dbName.totalByName[0]?.name}
      right={<SexIconWrapper>{sexIcon}</SexIconWrapper>}
    >
      <Wrapper>
        <Middle>
          <Spelling>
            <Title>ORTHOGRAPHES</Title>
            <NameSpellings color={sexColor} dbName={dbName} />
          </Spelling>
          <CharWrapper>
            <Title>POPULARITÉ</Title>
            <Bar
              options={{animation: false}}
              data={{
                labels: countByYears.map(e => e[0]),
                datasets: [
                  {
                    data: countByYears.map(e => e[1]),
                    backgroundColor: sexColor,
                    categoryPercentage: 0.95,
                    borderRadius: 2,
                  },
                ],
              }}
            />
          </CharWrapper>
          <div>
            <Title>GPT</Title>
            {gptInfo !== undefined ? <></> : <Button onClickAsync={handleGptClick}>GPT</Button>}
            {gptInfo !== undefined ? <ChatGptMarkdown message={gptInfo} /> : <></>}
          </div>
        </Middle>
        <Bottom>
          {bottom}
          <NameNavBar pathPrefix={navPathPrefix} db={db} nameKey={nameKey} project={project} />
        </Bottom>
      </Wrapper>
    </TopBarApp>
  );
};

NameView.displayName = 'NameView';

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const Middle = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 1rem;
  padding-bottom: 2rem;
  gap: 2rem;
`;

const Bottom = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  box-shadow: #666666 0px 0px 1rem 0.5rem;
  padding-bottom: env(safe-area-inset-bottom);
  background-color: ${Palette.secondaryColor};
`;

const SexIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Spelling = styled.div`
  display: flex;
  flex-direction: column;
`;

const CharWrapper = styled.div`
  /* display: flex; */
`;

const Title = styled.div`
  font-size: 1.2rem;
  color: #666;
  font-weight: bold;
  margin-bottom: 0.5rem;
`;
